import { interpolate, spring } from 'remotion';
import type { CSSProperties } from 'react';
import type { AnimationPreset, LayerAnimation } from '../types';

const SLIDE_PX = 56;
const SPRING_CONFIG = { damping: 18, mass: 0.8, stiffness: 120 };

// ─── Progress helpers ─────────────────────────────────────────

function clamp(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/** 0→1 as element enters */
function enterProgress(frame: number, anim: LayerAnimation, fps: number): number {
  const start = anim.enterDelay;
  if (frame < start) return 0;
  if (anim.enterDuration === 0) return 1;
  return clamp(
    spring({ frame: frame - start, fps, config: SPRING_CONFIG, durationInFrames: anim.enterDuration })
  );
}

/** 0→1 as element exits */
function exitProgress(frame: number, anim: LayerAnimation, fps: number, totalFrames: number): number {
  const exitStart = anim.exitAt === -1
    ? totalFrames - anim.exitDuration
    : anim.exitAt;
  if (frame < exitStart) return 0;
  if (anim.exitDuration === 0) return frame >= exitStart ? 1 : 0;
  return clamp(
    spring({ frame: frame - exitStart, fps, config: SPRING_CONFIG, durationInFrames: anim.exitDuration })
  );
}

// ─── Per-preset style ─────────────────────────────────────────

/** Returns CSS for enter state at progress p (0=hidden, 1=visible) */
function enterStyle(preset: AnimationPreset, p: number): CSSProperties {
  switch (preset) {
    case 'fade':
      return { opacity: p };
    case 'slide-left':
      return { opacity: Math.min(p * 2, 1), transform: `translateX(${SLIDE_PX * (1 - p)}px)` };
    case 'slide-right':
      return { opacity: Math.min(p * 2, 1), transform: `translateX(${-SLIDE_PX * (1 - p)}px)` };
    case 'slide-up':
      return { opacity: Math.min(p * 2, 1), transform: `translateY(${SLIDE_PX * (1 - p)}px)` };
    case 'slide-down':
      return { opacity: Math.min(p * 2, 1), transform: `translateY(${-SLIDE_PX * (1 - p)}px)` };
    case 'zoom-in':
      return { opacity: Math.min(p * 2, 1), transform: `scale(${0.85 + 0.15 * p})` };
    case 'zoom-out':
      return { opacity: Math.min(p * 2, 1), transform: `scale(${1.15 - 0.15 * p})` };
    case 'none':
    default:
      return {};
  }
}

/** Returns CSS for exit state at progress p (0=visible, 1=hidden) */
function exitStyle(preset: AnimationPreset, p: number): CSSProperties {
  switch (preset) {
    case 'fade':
      return { opacity: 1 - p };
    case 'slide-left':
      return { opacity: Math.max(1 - p * 2, 0), transform: `translateX(${-SLIDE_PX * p}px)` };
    case 'slide-right':
      return { opacity: Math.max(1 - p * 2, 0), transform: `translateX(${SLIDE_PX * p}px)` };
    case 'slide-up':
      return { opacity: Math.max(1 - p * 2, 0), transform: `translateY(${-SLIDE_PX * p}px)` };
    case 'slide-down':
      return { opacity: Math.max(1 - p * 2, 0), transform: `translateY(${SLIDE_PX * p}px)` };
    case 'zoom-in':
      return { opacity: Math.max(1 - p * 2, 0), transform: `scale(${1 + 0.15 * p})` };
    case 'zoom-out':
      return { opacity: Math.max(1 - p * 2, 0), transform: `scale(${1 - 0.15 * p})` };
    case 'none':
    default:
      return {};
  }
}

// ─── Merge styles (exit wins on opacity) ─────────────────────

function mergeStyles(...styles: CSSProperties[]): CSSProperties {
  return Object.assign({}, ...styles);
}

// ─── Public API ───────────────────────────────────────────────

/**
 * Returns CSS properties for a layer at the given frame.
 * Handles enter → visible → exit transitions automatically.
 */
export function getLayerStyle(
  frame: number,
  fps: number,
  totalFrames: number,
  anim: LayerAnimation,
  baseOpacity = 1
): CSSProperties {
  const ep = enterProgress(frame, anim, fps);
  const xp = exitProgress(frame, anim, fps, totalFrames);

  const enter = enterStyle(anim.enter, ep);
  const exit = exitStyle(anim.exit, xp);

  // Combine: if exiting, exit overrides enter
  const combined = mergeStyles(enter, xp > 0 ? exit : {});

  // Apply base opacity
  const finalOpacity =
    typeof combined.opacity === 'number'
      ? combined.opacity * baseOpacity
      : baseOpacity;

  return { ...combined, opacity: finalOpacity };
}

/**
 * Smooth linear fade for audio volume (not spring — audio should be linear).
 */
export function getAudioVolume(
  frame: number,
  fadeInFrames: number,
  fadeOutFrames: number,
  totalFrames: number,
  baseVolume: number
): number {
  const fadeIn = interpolate(frame, [0, fadeInFrames], [0, baseVolume], { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' });
  const fadeOut = interpolate(frame, [totalFrames - fadeOutFrames, totalFrames], [baseVolume, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return Math.min(fadeIn, fadeOut);
}
